import { useEffect, useState } from "react";
import { apiPrivate } from "@/lib/api";
import { Star, X, Loader2, ClipboardCheck, Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";

/**
 * Dashboard banner — pops only when there's an active survey the user hasn't responded to.
 * Non-blocking: user can dismiss session-locally via close button.
 */
export default function SurveyBanner() {
    const [survey, setSurvey] = useState(null);
    const [dismissedSession, setDismissedSession] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const load = () => {
        apiPrivate.get("/surveys/active")
            .then(r => setSurvey(r.data))
            .catch(() => {});
    };
    useEffect(() => { load(); }, []);

    if (!survey || survey.has_responded || dismissedSession) return null;

    return (
        <>
            <div
                className="mt-8 bento p-5 flex items-start gap-4 border-l-4 border-l-bayside hover:border-bayside/80 transition"
                data-testid="survey-banner"
            >
                <div className="h-10 w-10 rounded-full bg-bayside/15 border border-bayside/30 flex items-center justify-center shrink-0">
                    <ClipboardCheck className="h-5 w-5 text-bayside" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                        <h3 className="font-display text-lg leading-tight">{survey.title}</h3>
                        <button
                            onClick={() => setDismissedSession(true)}
                            data-testid="survey-dismiss"
                            aria-label="Chiudi"
                            className="text-zinc-500 hover:text-white shrink-0"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    {survey.description && (
                        <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{survey.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-3">
                        <button
                            onClick={() => setShowModal(true)}
                            className="btn-rebel text-xs"
                            data-testid="survey-open"
                        >
                            Partecipa al sondaggio
                        </button>
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">
                            ~2 minuti · {survey.questions.length} domande
                        </span>
                    </div>
                </div>
            </div>
            {showModal && (
                <SurveyModal
                    survey={survey}
                    onClose={() => setShowModal(false)}
                    onSubmitted={() => { setShowModal(false); load(); toast.success("Grazie per il tuo feedback!"); }}
                />
            )}
        </>
    );
}

function SurveyModal({ survey, onClose, onSubmitted }) {
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const setAnswer = (qid, field, value) => {
        setAnswers(a => ({ ...a, [qid]: { ...(a[qid] || { question_id: qid }), [field]: value } }));
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                answers: Object.values(answers).map(a => ({
                    question_id: a.question_id,
                    rating: a.rating ?? null,
                    text: a.text ?? null,
                })),
            };
            await apiPrivate.post(`/surveys/${survey.id}/submit`, payload);
            onSubmitted();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
            setSubmitting(false);
        }
    };

    const canSubmit = survey.questions.every(q => {
        const a = answers[q.id];
        if (q.type === "rating") return a?.rating != null;
        return true; // text fields are optional
    });

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="survey-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-7">
                    <div className="flex items-start justify-between mb-2">
                        <span className="label-tag text-magenta">— Sondaggio</span>
                        <button onClick={onClose} data-testid="survey-modal-close"><X className="h-5 w-5 text-zinc-500" /></button>
                    </div>
                    <h2 className="font-display text-3xl tracking-tight mt-2">{survey.title}</h2>
                    {survey.description && <p className="text-zinc-400 mt-2">{survey.description}</p>}

                    <div className="space-y-7 mt-7">
                        {survey.questions.map((q, idx) => (
                            <div key={q.id} data-testid={`survey-question-${q.id}`}>
                                <div className="flex items-start gap-2 mb-3">
                                    <span className="text-[10px] uppercase tracking-widest font-grotesk text-bayside mt-1">{idx + 1}.</span>
                                    <h3 className="font-display text-lg leading-tight">{q.text}</h3>
                                </div>
                                {q.type === "rating" ? (
                                    <RatingPicker
                                        value={answers[q.id]?.rating ?? null}
                                        onChange={(v) => setAnswer(q.id, "rating", v)}
                                        testid={`rating-${q.id}`}
                                    />
                                ) : (
                                    <div className="relative">
                                        <MessageSquare className="absolute top-3 left-3 h-4 w-4 text-zinc-600" />
                                        <textarea
                                            rows={3}
                                            value={answers[q.id]?.text ?? ""}
                                            onChange={(e) => setAnswer(q.id, "text", e.target.value)}
                                            placeholder="Scrivi la tua risposta…"
                                            data-testid={`text-${q.id}`}
                                            className="w-full bg-[#0F0F0F] border border-white/10 text-white pl-10 pr-3 py-2.5 font-grotesk text-sm focus:outline-none focus:border-bayside resize-none rounded-xl"
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-end gap-3 mt-8">
                        <button onClick={onClose} className="btn-ghost" data-testid="survey-cancel" disabled={submitting}>Annulla</button>
                        <button onClick={submit} disabled={submitting || !canSubmit} className="btn-rebel" data-testid="survey-submit">
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Invia risposte</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RatingPicker({ value, onChange, testid }) {
    const labels = ["Per niente", "Poco", "Abbastanza", "Molto", "Estremamente"];
    return (
        <div className="flex flex-wrap items-center gap-3" data-testid={testid}>
            <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map(n => (
                    <button
                        key={n}
                        onClick={() => onChange(n)}
                        data-testid={`${testid}-${n}`}
                        className={`h-10 w-10 rounded-full border transition flex items-center justify-center ${
                            value && n <= value
                                ? "border-bayside bg-bayside/15 text-bayside"
                                : "border-white/10 text-zinc-500 hover:border-white/30 hover:text-zinc-300"
                        }`}
                    >
                        <Star className={`h-4 w-4 ${value && n <= value ? "fill-bayside" : ""}`} />
                    </button>
                ))}
            </div>
            {value && (
                <span className="text-xs font-grotesk text-zinc-400">
                    {labels[value - 1]}
                </span>
            )}
        </div>
    );
}
